/**
 * SyntaxLegend - Leyenda de visualización Space Syntax
 *
 * @module core/spatial/components
 * @version 1.0
 * @created 2026-03-17
 *
 * @description
 * Muestra la leyenda de colores para la visualización de métricas
 * Space Syntax. Incluye:
 * - Selector de métrica (Integration/Choice, Global/Local)
 * - Barra de gradiente con colores de la rampa
 * - Valores mínimo y máximo
 * - Estado del cálculo
 *
 * @example
 * ```typescript
 * // En el mapa o panel lateral
 * <SyntaxLegend position="bottom-left" />
 * ```
 */

import { Braces, RefreshCw, AlertCircle, ChevronDown, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useSyntaxAnalysis, type SyntaxMetricType } from '../contexts/SyntaxAnalysisContext';
import {
  COLOR_RAMPS,
  DEFAULT_SYNTAX_RAMP,
  type SyntaxRampName,
} from '../config/syntax-color-ramps';
import './syntax-legend.css';

// ============================================================================
// TYPES
// ============================================================================

export interface SyntaxLegendProps {
  /** Posición en el mapa */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Si mostrar controles de configuración */
  showControls?: boolean;
  /** Rampa de color a mostrar */
  colorRamp?: SyntaxRampName;
  /** Callback al cambiar métrica */
  onMetricChange?: (metric: SyntaxMetricType) => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const METRIC_LABELS: Record<SyntaxMetricType, string> = {
  // Integration metrics (4 radios: global, global_raw, r1200, r800)
  integration_global: 'Integration (Global)',
  integration_global_raw: 'Integration (Global Raw)',
  integration_r1200: 'Integration (1200m)',
  integration_r800: 'Integration (800m)',
  // Choice metrics (3 radios: global, r1200, r800)
  choice_global: 'Choice (Global)',
  choice_r1200: 'Choice (1200m)',
  choice_r800: 'Choice (800m)',
};

const METRIC_DESCRIPTIONS: Record<SyntaxMetricType, string> = {
  // Integration descriptions (4 radios: global, global_raw, r1200, r800)
  integration_global: 'Accesibilidad global efectiva ajustada por alcance real sobre la red total',
  integration_global_raw: 'Accesibilidad global raw dentro de la componente alcanzable',
  integration_r1200: 'Accesibilidad dentro de radio 1200m',
  integration_r800: 'Accesibilidad dentro de radio 800m',
  // Choice descriptions (3 radios: global, r1200, r800)
  choice_global: 'Potencial de paso en rutas globales',
  choice_r1200: 'Potencial de paso en rutas de 1200m',
  choice_r800: 'Potencial de paso en rutas de 800m',
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Componente de leyenda para visualización Space Syntax
 */
export function SyntaxLegend({
  position = 'bottom-left',
  showControls = true,
  colorRamp = DEFAULT_SYNTAX_RAMP,
  onMetricChange,
}: SyntaxLegendProps): JSX.Element | null {
  const {
    status,
    syntaxModeActive,
    selectedMetric,
    setSelectedMetric,
    range,
    calculatedAt,
    setSyntaxModeActive,
    calculateSyntax,
    error,
    // Pipeline status (traceability)
    syntaxVersion,
    syntaxBasedOnNetworkVersion,
    urbanNetworkVersion,
    isStale,
  } = useSyntaxAnalysis();

  // No mostrar si no está activo el modo sintaxis
  if (!syntaxModeActive) return null;

  const ramp = COLOR_RAMPS[colorRamp];

  // Obtener rango de la métrica seleccionada (4 integration radios, 3 choice radios)
  const getMetricRange = (): { min: number; max: number } => {
    if (!range) return { min: 0, max: 1 };

    switch (selectedMetric) {
      // Integration metrics
      case 'integration_global':
        return range.integrationGlobal;
      case 'integration_global_raw':
        return range.integrationGlobalRaw;
      case 'integration_r1200':
        return range.integrationR1200;
      case 'integration_r800':
        return range.integrationR800;
      // Choice metrics
      case 'choice_global':
        return range.choiceGlobal;
      case 'choice_r1200':
        return range.choiceR1200;
      case 'choice_r800':
        return range.choiceR800;
      default:
        return { min: 0, max: 1 };
    }
  };

  const { min, max } = getMetricRange();

  // Generar CSS gradient
  const gradientStops = ramp.stops
    .map((s) => `${s.color} ${s.stop * 100}%`)
    .join(', ');

  const handleMetricChange = (metric: SyntaxMetricType): void => {
    setSelectedMetric(metric);
    onMetricChange?.(metric);
  };

  const formatValue = (value: number): string => {
    if (value === 0) return '0';
    if (value < 0.01) return value.toExponential(2);
    if (value < 1) return value.toFixed(3);
    if (value < 100) return value.toFixed(2);
    return value.toFixed(0);
  };

  const handleRecalculate = async (): Promise<void> => {
    try {
      await calculateSyntax();
    } catch (e) {
      console.error('[SyntaxLegend] Error al recalcular sintaxis:', e);
    }
  };

  return (
    <div className={`syntax-legend syntax-legend--${position}`}>
      {/* Header with status badge */}
      <div className="syntax-legend__header">
        <div className="syntax-legend__title">
          <Braces size={14} className="syntax-legend__icon" />
          <span>Space Syntax</span>
          {/* Status badge */}
          {status === 'ready' && !isStale && (
            <span className="syntax-legend__badge syntax-legend__badge--valid" title="Sintaxis sincronizada con la red actual">
              <CheckCircle2 size={10} />
              Vigente
            </span>
          )}
          {status === 'ready' && isStale && (
            <span className="syntax-legend__badge syntax-legend__badge--stale" title={`Calculado sobre red v${syntaxBasedOnNetworkVersion}, red actual v${urbanNetworkVersion}`}>
              <AlertTriangle size={10} />
              Desactualizado
            </span>
          )}
          {status === 'calculating' && (
            <span className="syntax-legend__badge syntax-legend__badge--calculating">
              <RefreshCw size={10} className="syntax-legend__spinner" />
              Calculando
            </span>
          )}
        </div>
        <button
          className="syntax-legend__close"
          onClick={() => setSyntaxModeActive(false)}
          title="Cerrar visualización"
          aria-label="Cerrar visualización de sintaxis"
        >
          &times;
        </button>
      </div>

      {/* Stale Warning + Recalculate Button */}
      {(isStale || status === 'error') && status !== 'calculating' && (
        <div className="syntax-legend__alert syntax-legend__alert--warning">
          <div className="syntax-legend__alert-content">
            <AlertTriangle size={14} className="syntax-legend__alert-icon" />
            <div className="syntax-legend__alert-text">
              {isStale && status !== 'error' && (
                <p>
                  <strong>Sintaxis desactualizada</strong>
                  <br />
                  La red ha cambiado desde el último cálculo. Recalcula para obtener métricas actualizadas.
                </p>
              )}
              {status === 'error' && (
                <p>
                  <strong>Error en el cálculo</strong>
                  <br />
                  {error || 'Ocurrió un error durante el cálculo. Intenta recalcular.'}
                </p>
              )}
            </div>
          </div>
          <button
            className="syntax-legend__recalculate-btn"
            onClick={handleRecalculate}
            title="Recalcular sintaxis espacial"
          >
            <RefreshCw size={14} />
            Recalcular Sintaxis
          </button>
        </div>
      )}

      {/* Status indicator */}
      {status === 'calculating' && (
        <div className="syntax-legend__status syntax-legend__status--calculating">
          <RefreshCw size={12} className="syntax-legend__spinner" />
          <span>Calculando...</span>
        </div>
      )}

      {status === 'error' && (
        <div className="syntax-legend__status syntax-legend__status--error">
          <AlertCircle size={12} />
          <span>{error || 'Error en cálculo'}</span>
        </div>
      )}

      {/* Metric selector */}
      {showControls && status === 'ready' && (
        <div className="syntax-legend__control">
          <label className="syntax-legend__label">Métrica</label>
          <div className="syntax-legend__select-wrapper">
            <select
              className="syntax-legend__select"
              value={selectedMetric}
              onChange={(e) => handleMetricChange(e.target.value as SyntaxMetricType)}
            >
              {Object.entries(METRIC_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronDown size={12} className="syntax-legend__select-icon" />
          </div>
          <span className="syntax-legend__description">
            {METRIC_DESCRIPTIONS[selectedMetric]}
          </span>
        </div>
      )}

      {/* Color gradient */}
      {status === 'ready' && (
        <div className="syntax-legend__gradient-container">
          <div
            className="syntax-legend__gradient"
            style={{ background: `linear-gradient(to right, ${gradientStops})` }}
          />
          <div className="syntax-legend__labels">
            <span className="syntax-legend__value syntax-legend__value--min">
              {formatValue(min)}
            </span>
            <span className="syntax-legend__value syntax-legend__value--max">
              {formatValue(max)}
            </span>
          </div>
        </div>
      )}

      {/* Metadata and Traceability */}
      {status === 'ready' && calculatedAt && (
        <div className="syntax-legend__meta">
          <div className="syntax-legend__meta-row">
            <span className="syntax-legend__meta-label">Calculado:</span>
            <span className="syntax-legend__meta-value">
              {calculatedAt.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
              {', '}
              {calculatedAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="syntax-legend__meta-row">
            <span className="syntax-legend__meta-label">Basado en:</span>
            <span className="syntax-legend__meta-value">
              Red urbana v{syntaxBasedOnNetworkVersion ?? '?'}
              {isStale && <span className="syntax-legend__meta-warning"> (actual: v{urbanNetworkVersion})</span>}
            </span>
          </div>
          <div className="syntax-legend__meta-row">
            <span className="syntax-legend__meta-label">Sintaxis:</span>
            <span className="syntax-legend__meta-value">v{syntaxVersion}</span>
          </div>
        </div>
      )}

      {/* No metrics message */}
      {status === 'idle' && (
        <div className="syntax-legend__empty">
          <p>La sintaxis se calculará automáticamente al guardar cambios en la red.</p>
        </div>
      )}
    </div>
  );
}

export default SyntaxLegend;
